import { last } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { BorrowerDto } from '../openapi/atlantis';
import { OnboardingResponseRoleEnum } from '../openapi/yenta';
import { deleteFileFromDropboxThroughYenta } from '../slices/DropboxSlice';
import { useAppDispatch, useAppSelector } from '../slices/store';
import { showErrorToast } from '../slices/ToastNotificationSlice';
import { convertFileResponseToFileWithState } from '../utils/DropboxUtils';
import { validatePDFFile } from '../utils/FileUtils';
import { getLegacyApplicationId } from '../utils/OnboardingHelpers';

interface UseAgentFileUploadOptions {
  name: string;
  checklistItemId: string;
  files: any[];
  setValue: (name: string, value: any, options?: any) => void;
  watch?: (name: string) => any;
  setFileReferences?: (refs: any) => void;
  validatePDF?: boolean;
  onFileUploadComplete?: (file: File) => Promise<void>;
  resetAdditionalState?: () => void;
}

/**
 * Custom hook to handle file upload/delete operations for agent onboarding
 * Includes Dropbox integration, PDF validation, and form state management
 */
export const useAgentFileUpload = ({
  name,
  checklistItemId,
  files,
  setValue,
  watch,
  setFileReferences,
  validatePDF = true,
  onFileUploadComplete,
  resetAdditionalState,
}: UseAgentFileUploadOptions) => {
  const dispatch = useAppDispatch();
  const authUserDetail = useAppSelector((state) => state.auth.authUserDetail);
  const [hasSetInitialFile, setHasSetInitialFile] = useState(false);

  const legacyApplicationId = getLegacyApplicationId(
    authUserDetail,
    OnboardingResponseRoleEnum.Agent,
  );

  // Reset state when checklistItemId changes
  const resetFormState = useCallback(() => {
    setValue(name, undefined, { shouldValidate: false });
    setFileReferences?.(undefined);
    setHasSetInitialFile(false);
    resetAdditionalState?.();
  }, [name, setValue, setFileReferences, resetAdditionalState]);

  // Set initial file value if exists from checklist
  useEffect(() => {
    const lastFile = last(files);
    if (lastFile && !hasSetInitialFile) {
      const fileObject = convertFileResponseToFileWithState(lastFile);
      setValue(name, fileObject, { shouldValidate: false });
      setHasSetInitialFile(true);
    }
  }, [files, name, setValue, hasSetInitialFile]);

  // Handle file deletion from Dropbox
  const handleFileDelete = useCallback(
    async (fileId: string) => {
      // Find the file being deleted
      const fileToDelete = files?.find((f) => f.id === fileId);

      if (fileToDelete) {
        // Create borrower data from authUserDetail
        const borrowerData = {
          id: authUserDetail?.id!,
          dropboxId: authUserDetail?.dropboxId,
        } as BorrowerDto;

        // Delete from Dropbox
        const success = await dispatch(
          deleteFileFromDropboxThroughYenta(
            borrowerData,
            fileToDelete.id!,
            legacyApplicationId!,
            checklistItemId,
          ),
        );

        if (success) {
          // Only clear form if deletion was successful
          resetFormState();
        }
      } else {
        // If it's a new file (not yet uploaded), just clear the form
        resetFormState();
      }
    },
    [
      authUserDetail?.dropboxId,
      authUserDetail?.id,
      checklistItemId,
      dispatch,
      files,
      legacyApplicationId,
      resetFormState,
    ],
  );

  // Handle file upload with optional PDF validation
  const handleFileChange = useCallback(
    async (file: File | undefined) => {
      if (!file) {
        // File removed
        resetFormState();
        return;
      }

      // Only process new files (not existing files being reselected)
      if ((file as any).isExistingFile) {
        return;
      }

      // Delete existing file FIRST, before any validation
      const currentFile = watch?.(name);
      if (currentFile && files?.length > 0) {
        const existingFile = last(files);
        if (existingFile?.id) {
          await handleFileDelete(existingFile.id);
        }
      }

      // Validate PDF file if enabled
      if (validatePDF) {
        const validation = await validatePDFFile(file);
        if (!validation.isValid) {
          setValue(name, undefined, { shouldValidate: true });
          dispatch(showErrorToast(validation.errorMessage!));
          return; // Don't proceed with the file upload
        }
      }

      // Set the new file
      setValue(name, file, { shouldValidate: true });

      // Call completion callback if provided (for additional processing like license verification)
      if (onFileUploadComplete) {
        await onFileUploadComplete(file);
      }
    },
    [
      name,
      files,
      watch,
      validatePDF,
      handleFileDelete,
      resetFormState,
      setValue,
      dispatch,
      onFileUploadComplete,
    ],
  );

  return {
    handleFileChange,
    handleFileDelete,
    resetFormState,
    hasSetInitialFile,
  };
};
